/**
 * E-ticaret canlı destek uyarıları — uygulama genelinde tek bağlantı.
 *
 * Header'daki bildirim zili başlatır; hangi sayfada olunursa olunsun yeni
 * görüşme/mesaj geldiğinde ses çalar, sekme başlığını işaretler, sekme
 * arka plandaysa masaüstü bildirimi gösterir. E-Ticaret sayfası sayıyı ve
 * "görüşmeyi aç" isteklerini buradan okur.
 */
import { useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { ecommerceApi } from '../config/apiClient';
import { ECOMMERCE_API_URL } from '../config/api';

export interface ChatAlertRow {
  id: string;
  status: string;
  visitorName?: string | null;
  visitorEmail?: string | null;
  topic?: string | null;
  unread?: number;
  lastMessageAt?: string;
}

export interface ChatAlertArrival {
  /** Her yeni gelişte artar; arayüz animasyon/toast tetiklemek için izler. */
  seq: number;
  title: string;
  body: string;
  sessionId: string;
}

interface State {
  active: boolean;
  rows: ChatAlertRow[];
  count: number;
  arrival: ChatAlertArrival | null;
  muted: boolean;
}

const MUTE_KEY = 'ecomChatAlertsMuted';
const POLL_MS = 15000;
const OPEN_REQUEST_KEY = 'ecomChatOpenRequest';
export const OPEN_LIVE_CHAT_EVENT = 'ecommerce:open-live-chat';

let state: State = {
  active: false,
  rows: [],
  count: 0,
  arrival: null,
  muted: localStorage.getItem(MUTE_KEY) === '1',
};
const listeners = new Set<() => void>();
const setState = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

/** Canlı destek > Görüşmeler ekranı açık ve görünürken toast gösterilmez (ses yine çalar). */
let viewingInbox = false;
export const setViewingLiveChatInbox = (value: boolean) => {
  viewingInbox = value;
};

/* ---------------- Ses ---------------- */

let audioCtx: AudioContext | null = null;
const getAudioCtx = () => {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  return audioCtx;
};

// Tarayıcılar sesi ancak kullanıcı sayfayla etkileşime girdikten sonra açar;
// ilk tıklama/tuşta ses bağlamını uyandır.
const unlockAudio = () => {
  const ctx = getAudioCtx();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => undefined);
};
['pointerdown', 'keydown', 'touchstart'].forEach((evt) => window.addEventListener(evt, unlockAudio, { passive: true }));

export function playChatChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
    const now = ctx.currentTime;
    // İki tonlu, kısa ve fark edilir "ding-dong".
    [
      { freq: 880, at: 0 },
      { freq: 1320, at: 0.16 },
    ].forEach(({ freq, at }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.25, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.45);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.5);
    });
  } catch {
    /* ses çalınamazsa sessiz geç */
  }
}

export function setChatAlertsMuted(muted: boolean) {
  localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  setState({ muted });
  if (!muted) playChatChime();
}

/* ---------------- Sekme başlığı ---------------- */

let baseTitle: string | null = null;
let titleTimer: number | null = null;

const stopTitleFlash = () => {
  if (titleTimer) window.clearInterval(titleTimer);
  titleTimer = null;
  if (baseTitle !== null) document.title = baseTitle;
  baseTitle = null;
};

const startTitleFlash = (text: string) => {
  if (!document.hidden) return;
  if (baseTitle === null) baseTitle = document.title;
  if (titleTimer) window.clearInterval(titleTimer);
  let on = false;
  titleTimer = window.setInterval(() => {
    on = !on;
    document.title = on ? `💬 ${text}` : (baseTitle as string);
  }, 1000);
};

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) stopTitleFlash();
});

/* ---------------- Masaüstü bildirimi ---------------- */

export const desktopNotificationSupported = () => typeof window !== 'undefined' && 'Notification' in window;

export async function requestDesktopNotifications() {
  if (!desktopNotificationSupported()) return 'denied' as NotificationPermission;
  const result = await Notification.requestPermission();
  setState({});
  return result;
}

const showDesktopNotification = (arrival: ChatAlertArrival) => {
  if (!desktopNotificationSupported() || Notification.permission !== 'granted' || !document.hidden) return;
  try {
    const n = new Notification(arrival.title, { body: arrival.body, tag: 'ecom-live-chat', icon: '/favicon.ico' });
    n.onclick = () => {
      window.focus();
      requestOpenLiveChat(arrival.sessionId);
      n.close();
    };
  } catch {
    /* bazı tarayıcılar (mobil) constructor'ı desteklemez */
  }
};

/* ---------------- Görüşmeyi aç isteği ---------------- */

export function requestOpenLiveChat(sessionId?: string) {
  sessionStorage.setItem(OPEN_REQUEST_KEY, JSON.stringify({ sessionId: sessionId || null, at: Date.now() }));
  window.dispatchEvent(new CustomEvent(OPEN_LIVE_CHAT_EVENT, { detail: { sessionId: sessionId || null } }));
  if (!window.location.hash.startsWith('#/e-commerce')) window.location.hash = '#/e-commerce';
}

/** Bekleyen isteği bir kez okur (sayfa sonradan açıldığında). 1 dakikadan eski istekler yok sayılır. */
export function consumeOpenLiveChatRequest(): { sessionId: string | null } | null {
  const raw = sessionStorage.getItem(OPEN_REQUEST_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(OPEN_REQUEST_KEY);
  try {
    const parsed = JSON.parse(raw);
    if (Date.now() - (parsed.at || 0) > 60000) return null;
    return { sessionId: parsed.sessionId || null };
  } catch {
    return null;
  }
}

/* ---------------- Bağlantı ---------------- */

const rowKey = (row: ChatAlertRow) => `${row.id}:${row.unread || 0}:${row.lastMessageAt || ''}:${row.status}`;
const needsAttention = (row: ChatAlertRow) =>
  row.status === 'WaitingAgent' || ((row.unread || 0) > 0 && row.status !== 'Closed');
const rowWeight = (row: ChatAlertRow) => Math.max(row.unread || 0, row.status === 'WaitingAgent' ? 1 : 0);

let runningFor: string | null = null;
let teardown: (() => void) | null = null;
let pollNow: (() => void) | null = null;

/** Bağlantı açıksa listeyi hemen yeniler (ör. görüşme okunduğunda). */
export function refreshChatAlerts() {
  pollNow?.();
}
let seq = 0;

export function startChatAlerts(companyId: string) {
  if (runningFor === companyId) return;
  stopChatAlerts();
  runningFor = companyId;

  let stopped = false;
  let primed = false;
  const seen = new Set<string>();

  const poll = async () => {
    try {
      const res = await ecommerceApi.get('/api/ecommerce/chat/inbox?status=all');
      if (stopped) return;
      const rows: ChatAlertRow[] = res.data?.data || [];
      const pending = rows
        .filter(needsAttention)
        .sort((a, b) => String(b.lastMessageAt || '').localeCompare(String(a.lastMessageAt || '')));
      const count = pending.reduce((sum, row) => sum + rowWeight(row), 0);

      if (!primed) {
        pending.forEach((row) => seen.add(rowKey(row)));
        primed = true;
        setState({ active: true, rows: pending, count });
        return;
      }

      const fresh = pending.filter((row) => !seen.has(rowKey(row)));
      pending.forEach((row) => seen.add(rowKey(row)));

      if (fresh.length === 0) {
        setState({ active: true, rows: pending, count });
        return;
      }

      const latest = fresh[0];
      const who = latest.visitorName || 'Ziyaretçi';
      const arrival: ChatAlertArrival = {
        seq: ++seq,
        sessionId: latest.id,
        title: latest.status === 'WaitingAgent' && !(latest.unread || 0) ? 'Temsilci bekleniyor' : fresh.length > 1 ? `${fresh.length} yeni sohbet mesajı` : 'Yeni sohbet mesajı',
        body: `${who}${latest.topic ? ` · ${latest.topic}` : ''} yanıt bekliyor.`,
      };
      setState({ active: true, rows: pending, count, arrival: viewingInbox && !document.hidden && window.location.hash.startsWith('#/e-commerce') ? state.arrival : arrival });
      if (!state.muted) playChatChime();
      startTitleFlash(arrival.title);
      showDesktopNotification(arrival);
    } catch (error: any) {
      const status = error?.response?.status;
      // Modül/yetki yoksa sessizce kapat; ağ hatasında denemeye devam et.
      if (status === 401 || status === 403 || status === 404) {
        stopped = true;
        window.clearInterval(timer);
        conn.stop().catch(() => undefined);
        setState({ active: false, rows: [], count: 0 });
      }
    }
  };

  // Aynı anda gelen birden fazla olayı tek istekte topla.
  let debounce: number | null = null;
  const schedulePoll = () => {
    if (debounce) window.clearTimeout(debounce);
    debounce = window.setTimeout(poll, 300);
  };

  const conn = new signalR.HubConnectionBuilder()
    .withUrl(`${String(ECOMMERCE_API_URL).replace(/\/$/, '')}/hubs/ecomchat`)
    .withAutomaticReconnect()
    .build();
  conn.on('ChatRequestCreated', schedulePoll);
  conn.on('NewChatMessage', schedulePoll);
  conn.on('SessionClaimed', schedulePoll);
  conn.onreconnected(() => {
    conn.invoke('JoinCompany', companyId).catch(() => undefined);
    schedulePoll();
  });
  conn.start().then(() => conn.invoke('JoinCompany', companyId)).catch(() => undefined);

  pollNow = schedulePoll;
  poll();
  const timer = window.setInterval(poll, POLL_MS);

  teardown = () => {
    stopped = true;
    window.clearInterval(timer);
    if (debounce) window.clearTimeout(debounce);
    conn.stop().catch(() => undefined);
  };
}

export function stopChatAlerts() {
  teardown?.();
  teardown = null;
  pollNow = null;
  runningFor = null;
  stopTitleFlash();
  setState({ active: false, rows: [], count: 0, arrival: null });
}

/** Görüşme açıldığında/okunduğunda sayıyı hemen tazelemek için. */
export function dismissChatArrival() {
  setState({ arrival: null });
}

export function useChatAlerts(): State {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    const listener = () => setSnapshot(state);
    listeners.add(listener);
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return snapshot;
}
